
// eslint-disable-next-line @tseslint/no-shadow
import { ChangeDetectionStrategy, Component, inject, model, ModelSignal, signal, Signal, WritableSignal } from '@angular/core';
import { provideTableConfigService } from '@core/services/table/tokens/table-scope.token';
import { TableComponent } from "@shared/components/table/table.component";
import { PROJECT_SPECIAL_TIMES_TABLE_CONFIG } from './configs/special-times-table-default.config';
import { TableConfigService } from '@core/services/table/table-config.service';
import { TableConfig } from '@shared/components/table-implementation/types/table-config';
import { toSignal } from '@angular/core/rxjs-interop';
import { OverviewHeaderComponent } from "@shared/components/overview-header/overview-header.component";
import { OverviewSettingsMenuComponent } from "@shared/components/overview-header/components/overview-settings-menu/overview-settings-menu.component";
import { TableHeaderGroupComponent } from "@shared/components/table/components/table-header-group/table-header-group.component";
import { TableHeaderRowComponent } from "@shared/components/table/components/table-header-row/table-header-row.component";
import { TableHeaderComponent } from "@shared/components/table/components/table-header/table-header.component";
import { BclTableHeaderComponent } from "@shared/components/table-implementation/bcl-table-header/bcl-table-header.component";
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { TableSkeletonComponent } from "@shared/components/table/components/table-skeleton/table-skeleton.component";
import { EntityState, TableDataService } from '@core/services/table/table-data.service';
import { TableDataGroupComponent } from "@shared/components/table/components/table-data-group/table-data-group.component";
import { TableDataRowComponent } from "@shared/components/table/components/table-data-row/table-data-row.component";
import { TableDataComponent } from "@shared/components/table/components/table-data/table-data.component";
import { OverviewComponent } from "@shared/components/templates/overview/overview.component";
import { OverviewHeaderAddComponent } from "@shared/components/overview-header/components/overview-header-add/overview-header-add.component";
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { OverviewHeaderAdditionalOptionsComponent } from "@shared/components/overview-header/components/overview-header-additional-options/overview-header-additional-options.component";
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faWandSparkles } from '@fortawesome/pro-light-svg-icons';
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { BclTableDataComponent } from "@shared/components/table-implementation/bcl-table-data/bcl-table-data.component";
import { isProjectSpecialTimeType, parseToProjectSpecialTime, PROJECT_SPECIAL_TIME_TYPES, ProjectSpecialTime, ProjectSpecialTimeDto, ProjectSpecialTimeOverviewDto, ProjectSpecialTimeWithoutId } from './types/project-special-time.type';
import { ProjectSpecialTimesHolidayMenuComponent } from "./components/project-special-times-holiday-menu/project-special-times-holiday-menu.component";
import { ProjectSpecialTimeEditMenuComponent } from "./components/project-special-time-menu/project-special-time-menu.component";
import { TABLE_DATA_API_CONFIG_TYPES } from '@core/services/table/types/table-api-config';
import { PROJECT_NUMBER } from '@features/project/constants/routes.constant';
import { finalize, map, tap } from 'rxjs';
import { InfiniteScrollDirective } from "@shared/directives/infinite-scroll/infinite-scroll.directive";
import { VirtualScrollWrapperComponent } from '@shared/components/virtual-scroll-wrapper/virtual-scroll-wrapper.component';
import { OgsItemDirective } from "@shared/directives/ogs-item.directive";
import { parseToTableData, TableData } from '@shared/components/table-implementation/bcl-table-data/types/table-data.type';
import { TableColumn } from '@shared/components/table-implementation/types/column/header.type';
import { DATA_DISPLAY_TYPES, DataDisplayType } from '@shared/components/table-implementation/bcl-table-data/data-display-type';
import { STATUS_TYPES } from '@shared/types/status.types';
import { ProjectSpecialTimeService } from './services/project-special-time.service';
import { ProjectSpecialTimeUpdate } from './types/project-special-time-update.type';
import { OverviewHeaderOperatorsComponent } from "@shared/components/overview-header/components/overview-header-operators/overview-header-operators.component";
import { OverviewHeaderSettingsComponent } from "@shared/components/overview-header/components/overview-header-operators/components/overview-header-settings/overview-header-settings.component";
import { OverviewHeaderSearchComponent } from "@shared/components/overview-header/components/overview-header-search/overview-header-search.component";
import { OverviewHeaderFilterChipsComponent } from "@shared/components/overview-header/components/overview-header-filter-chips/overview-header-filter-chips.component";
import { OverviewHeaderColumnChipsComponent } from "@shared/components/overview-header/components/overview-header-column-chips/overview-header-column-chips.component";
import { ComponentToastService } from '@shared/services/component-toast/component-toast.service';
import { ProjectDetailsDataService } from '../project-details/services/project-details.service';
import { ProjectDetails } from '../project-details/types/project-details.type';
import { MatTooltipModule } from '@angular/material/tooltip';

const TABLE_SCOPE: string = "project-special-times-overview";
const TRANSLATION_SCOPE: string = "feature/project-special-times-overview";

// eslint-disable-next-line @tseslint/no-unnecessary-type-parameters
function getDataForField<T extends Record<string, unknown>> (item: T, header: TableColumn): TableData | undefined {
  const key: string = header.field;
  const dataDisplayType: DataDisplayType = header.dataDisplayType;
  const value: unknown = item[ key ];

  if (isProjectSpecialTimeType(value)) {
    if (value === PROJECT_SPECIAL_TIME_TYPES.opening) {
      return {
        type: DATA_DISPLAY_TYPES.status,
        status: {
          statusType: STATUS_TYPES.success,
          translationInfoDisplayName: {
            defaultText: "Sonderöffnungszeit",
            token: "special-opening-time",
            scope: TRANSLATION_SCOPE
          }
        }
      };
    }

    return {
      type: DATA_DISPLAY_TYPES.status,
      status: {
        statusType: STATUS_TYPES.neutral,
        translationInfoDisplayName: {
          defaultText: "Sonderschließzeit",
          token: "special-closing-time",
          scope: TRANSLATION_SCOPE
        }
      }
    };
  }

  return parseToTableData(dataDisplayType, value);
}

@Component({
  selector: 'app-project-special-times',
  standalone: true,
  imports: [
    MatButtonModule,
    TableComponent,
    OverviewHeaderComponent,
    OverviewSettingsMenuComponent,
    TableHeaderGroupComponent,
    TableHeaderRowComponent,
    TableHeaderComponent,
    BclTableHeaderComponent,
    TranslationPipeModule,
    TableSkeletonComponent,
    TableDataGroupComponent,
    TableDataRowComponent,
    TableDataComponent,
    OverviewComponent,
    OverviewHeaderAddComponent,
    MatRadioModule,
    OverviewHeaderAdditionalOptionsComponent,
    IconModule,
    BclTableDataComponent,
    ProjectSpecialTimesHolidayMenuComponent,
    ProjectSpecialTimeEditMenuComponent,
    VirtualScrollWrapperComponent,
    InfiniteScrollDirective,
    OgsItemDirective,
    OverviewHeaderOperatorsComponent,
    OverviewHeaderSettingsComponent,
    OverviewHeaderSearchComponent,
    OverviewHeaderFilterChipsComponent,
    OverviewHeaderColumnChipsComponent,
    MatTooltipModule
  ],
  providers: [
      TableDataService<ProjectSpecialTimeDto>,
      provideTableConfigService(TABLE_SCOPE, PROJECT_SPECIAL_TIMES_TABLE_CONFIG),
      provideTranslationScope(TRANSLATION_SCOPE)
  ],
  templateUrl: './project-special-times.component.html',
  styleUrl: './project-special-times.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectSpecialTimesComponent {
  private readonly _toastService: ComponentToastService = inject(ComponentToastService);

  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _projectSpecialTimesService: ProjectSpecialTimeService = inject(ProjectSpecialTimeService);

  private readonly _projectDetailsService: ProjectDetailsDataService = inject(ProjectDetailsDataService);

  private readonly _tableConfigService: TableConfigService = inject(TableConfigService);

  private readonly _tableDataService: TableDataService<ProjectSpecialTimeOverviewDto> = inject(TableDataService<ProjectSpecialTimeOverviewDto>);

  private readonly _projectNumber: string = inject(PROJECT_NUMBER);

  protected readonly TRANSLATION_SCOPE: typeof TRANSLATION_SCOPE = TRANSLATION_SCOPE;

  protected readonly canAddHolidays: Signal<boolean> = toSignal(this._projectDetailsService.getProjectDetails(this._projectNumber)
    .pipe(
      map((projectDetails: ProjectDetails) =>
        projectDetails.address.country.isoCodeTwo === "DE"
        && projectDetails.address.region !== undefined)
    ), { initialValue: false });


  protected isHolidayMenuExpanded: ModelSignal<boolean> = model<boolean>(false);

  protected isEditMenuExpanded: ModelSignal<boolean> = model<boolean>(false);

  protected tableData: Signal<ProjectSpecialTime[]> = toSignal(this._tableDataService.data$.pipe(
    map((state: EntityState<ProjectSpecialTimeOverviewDto>) => state.entities
      .map(parseToProjectSpecialTime)
      .filter((specialTime: ProjectSpecialTime | undefined): specialTime is ProjectSpecialTime => specialTime !== undefined))
  ), { initialValue: [] });

  protected selectedTableConfig: Signal<TableConfig | undefined> = toSignal(this._tableConfigService.selectedConfig$);

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected editSpecialTime: WritableSignal<ProjectSpecialTime | undefined> = signal(undefined);

  protected getDataForField: typeof getDataForField = getDataForField;


  constructor () {
    this._faIconLibrary.addIcons(faWandSparkles);
    this._tableDataService.setConfig({ type: TABLE_DATA_API_CONFIG_TYPES.preDefined, url: `api/projects/${ this._projectNumber }/special-merged-times` });
  }

  protected addHolidays (): void {
    this._projectSpecialTimesService.addHolidays(this._projectNumber)
      .pipe(
        tap({
          finalize: () => {
            this.isHolidayMenuExpanded.set(false);
            this._tableDataService.reload();
          }
        })
      )
      .subscribe({
        next: () => {
          this._toastService.showToast(STATUS_TYPES.success, {
            defaultText: "Feiertage erfolgreich hinzugefügt",
            token: "status-holidays-added-successfully",
            scope: TRANSLATION_SCOPE
          });
        },
        error: () => {
          this._toastService.showToast(STATUS_TYPES.error, {
            defaultText: "Unerwarteter Fehler beim Hinzufügen der Feiertage",
            token: "status-holidays-added-error",
            scope: TRANSLATION_SCOPE
          });
        }
      });
  }

  protected onEditClick (rowData: ProjectSpecialTime): void {
    this.editSpecialTime.set(rowData);
    this.isEditMenuExpanded.set(true);
  }

  protected openMenu (): void {
    this.editSpecialTime.set(undefined);
    this.isEditMenuExpanded.set(true);
  }

  protected onTableOffsetReached (): void {
    this._tableDataService.nextPage();
  }

  protected updateSpecialTime (update: ProjectSpecialTimeUpdate): void {
    this._projectSpecialTimesService.update(this._projectNumber, update.oldSpecialTime, update.newSpecialTime).pipe(
      finalize(() => {
        this.isEditMenuExpanded.set(false);
        this._tableDataService.reload();
        this.editSpecialTime.set(undefined);
      })
    )
      .subscribe({
        next: () => {
          this._toastService.showToast(STATUS_TYPES.success, {
            defaultText: "Sonderzeit erfolgreich angepasst",
            token: "status-time-update-successful",
            scope: TRANSLATION_SCOPE
          });
        },
        error: () => {
          this._toastService.showToast(STATUS_TYPES.error, {
            defaultText: "Unerwarteter Fehler beim Anpassen der Sonderzeit",
            token: "status-time-update-error",
            scope: TRANSLATION_SCOPE
          });
        }
      });
  }

  protected deleteSpecialTime (specialTime: ProjectSpecialTime): void {
    this._projectSpecialTimesService[ "delete" ](this._projectNumber, specialTime)
      .pipe(
        finalize(() => {
          this.isEditMenuExpanded.set(false);
          this._tableDataService.reload();
          this.editSpecialTime.set(undefined);
        })
      )
      .subscribe({
        next: () => {
          this._toastService.showToast(STATUS_TYPES.success, {
            defaultText: "Sonderzeit erfolgreich gelöscht",
            token: "status-time-delete-successful",
            scope: TRANSLATION_SCOPE
          });
        },
        error: () => {
          this._toastService.showToast(STATUS_TYPES.error, {
            defaultText: "Unerwarteter Fehler beim Löschen der Sonderzeit",
            token: "status-time-delete-error",
            scope: TRANSLATION_SCOPE
          });
        }
      });
  }

  protected addSpecialTime (specialTime: ProjectSpecialTimeWithoutId): void {
    this._projectSpecialTimesService.create(this._projectNumber, specialTime)
      .pipe(
        finalize(() => {
          this.isEditMenuExpanded.set(false);
          this._tableDataService.reload();
          this.editSpecialTime.set(undefined);
        })
      )
      .subscribe({
        next: () => {
          this._toastService.showToast(STATUS_TYPES.success, {
            defaultText: "Sonderzeit erfolgreich erstellt",
            token: "status-time-create-successful",
            scope: TRANSLATION_SCOPE
          });
        },
        error: () => {
          this._toastService.showToast(STATUS_TYPES.error, {
            defaultText: "Unerwarteter Fehler beim erstellen der Sonderzeit",
            token: "status-time-create-error",
            scope: TRANSLATION_SCOPE
          });
        }
      });
  }
}

